import React, { useContext, useEffect, useState } from 'react';
import SectionHeader from '../../../_molecules/SectionHeader';
import LoadingArea from '@molecules/LoadingArea';
import SectionBreadcrumbs from "../../../_molecules/SectionBreadcrumbs";
import SectionBreadcrumb from "../../../_molecules/SectionBreadcrumb";
import useViewer from '@hooks/useViewer';
import useDocumentTitle from '@hooks/useDocumentTitle';
import { fetchMemberPaymentHistory } from '@general/api/memberAPI';

import {
    BodySmall,
    DividerLightGreyStyled,
    H3,
    H4,
    ErrorText,
} from '@styled';

const BillingPaymentHistoryLayout = () => {
    const { viewer } = useViewer();
    const { documentTitle } = useDocumentTitle('Payment History');
    const [paymentHistory, setPaymentHistory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadPaymentHistory = async () => {
            if (viewer) {
                try {
                    const data = await fetchMemberPaymentHistory(viewer.recordId);
                    const normalizedData = normalizePaymentHistoryData(data);

                    setPaymentHistory(normalizedData);
                } catch (err) {
                    setError('Failed to load payment history.');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadPaymentHistory();
    }, [viewer]);

    if (!viewer) {
        return <LoadingArea />;
    }

    if (isLoading) {
        return <LoadingArea message="Loading payment history..." />;
    }

    return (
        <div id="my-icsc-billing">
            <SectionHeader title="My Account" />
            <div className="flex justify-content-between mt-2">
                <SectionBreadcrumbs>
                    <SectionBreadcrumb route="/my-icsc/account">My Account</SectionBreadcrumb>
                    <SectionBreadcrumb route="/my-icsc/account/edit-billing">Billing & Payments</SectionBreadcrumb>
                    <SectionBreadcrumb isActive>{documentTitle}</SectionBreadcrumb>
                </SectionBreadcrumbs>
            </div>

            <H3 className="mt-3 mb-4">{documentTitle}</H3>

            {error && <ErrorText>{error}</ErrorText>}

            {renderPaymentSection('Dues History', paymentHistory?.dues, 'No dues history found.', '/my-icsc/dues-receipt/')}
            {renderPaymentSection('Event History', paymentHistory?.events, 'No event history found.', '/my-icsc/event-receipt/')}
            {renderPaymentSection('Donation History', paymentHistory?.donations, 'No donation history found.', '/my-icsc/donation-receipt/')}
            {renderPaymentSection('Certification History', paymentHistory?.certifications, 'No certification history found.', '/my-icsc/certification-receipt/')}
        </div>
    );
};

const renderPaymentSection = (title, items = [], noResultsMessage, receiptPath) => (
    <div className="contact-section mb-4 pb-4">
        <H4>{title}</H4>
        {items.length === 0 ? (
            <BodySmall className="mb-1rem text-muted">
                <em>
                    {noResultsMessage} If you believe this is an error, please email <a className="accent" href="mailto:membership@icsc.com">membership@icsc.com</a> or call +1 646-728-3800.
                </em>
            </BodySmall>
        ) : (
            <table className="table">
                <thead>
                    <tr className="odd">
                        <th>Payment Date</th>
                        <th>Amount</th>
                        <th>{title.includes('Event') ? 'Event' : title.includes('Donation') ? 'Payment Type' : 'Type'}</th>
                        <th>View/Print Receipt</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr className={index % 2 === 0 ? 'even' : 'odd'} key={index}>
                            <td>{item.order_date || 'N/A'}</td>
                            <td>{item.total_price || '$0.00'}</td>
                            <td>{item.product_name || 'N/A'}</td>
                            <td>
                                <a
                                    className="accent"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={
                                        title.includes('Event')
                                            ? `${receiptPath}${item.event_id}/${item.order_detail_id}`
                                            : `${receiptPath}${item.order_detail_id}`
                                    }
                                >
                                    View/Print Receipt
                                </a>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const normalizePaymentHistoryData = (data) => {
    const normalized = {
        events: [],
        dues: [],
        donations: [],
        certifications: [],
        events_no_results: false,
        dues_no_results: false,
        certifications_no_results: false,
        donations_no_results: false,
    };

    if (data?.value) {
        data.value.forEach(paymentHistory => {
            const order = {
                order_number: paymentHistory.ordernumber,
                total_line_item_amount: `$${paymentHistory.totallineitemamount.toFixed(2)}`,
                total_tax: `$${paymentHistory.totaltax.toFixed(2)}`,
                total_amount: `$${paymentHistory.totalamount.toFixed(2)}`,
                order_date: paymentHistory.vsi_ordersubmitteddate,
                foundation_subscription: paymentHistory.vsi_recurringdonation,
            };

            const lineItems = [];
            let lineItemCount = 1;
            let lastEventId = null;
            let totalEventCount = 0;

            if (paymentHistory.order_details && paymentHistory.totalamount >= 0) {
                paymentHistory.order_details.forEach(orderDetail => {
                    const eventId = paymentHistory.vsi_EventId?.vsi_eventcode;
                    const description =
                        orderDetail.description && typeof orderDetail.description === 'string'
                            ? orderDetail.description.includes(';')
                                ? orderDetail.description.split(';')[1]
                                : orderDetail.description
                            : ''; // Or you can use a fallback string like 'No description'

                    if (eventId === lastEventId) {
                        lineItemCount += 1;
                    } else {
                        lineItemCount = 1;
                        lastEventId = eventId;
                    }

                    totalEventCount = eventId
                        ? (JSON.stringify(data.value).match(new RegExp(`"description":"${eventId}"`, 'g')) || []).length
                        : 0;

                    const lineItem = {
                        product_name: orderDetail.productname.replace(/Full Program$/, '').trim(),
                        educational_credit: orderDetail.vsi_educationalcredit,
                        order_date: new Date(orderDetail.createdon).toISOString().split('T')[0],
                        quantity: orderDetail.quantity,
                        price_per_unit: `$${orderDetail.priceperunit.toFixed(2)}`,
                        total_price:
                            orderDetail.vsi_price !== null
                                ? `$${orderDetail.vsi_price.toFixed(2)}`
                                : `$${paymentHistory.totalamount.toFixed(2)}`,
                        tax: orderDetail.tax,
                        description,
                        order_detail_id: orderDetail.salesorderdetailid,
                        product_type: orderDetail['vsi_ordertype@OData.Community.Display.V1.FormattedValue'],
                        event_id: eventId,
                        order: [order],
                        lineitem_count: lineItemCount.toString(),
                        total_events: totalEventCount.toString(),
                    };

                    switch (orderDetail['vsi_ordertype@OData.Community.Display.V1.FormattedValue']) {
                        case 'Event':
                            normalized.events.push(lineItem);
                            break;
                        case 'Membership':
                            normalized.dues.push(lineItem);
                            break;
                        case 'Donation-U':
                        case 'Donation-R':
                        case 'Contrib':
                            normalized.donations.push(lineItem);
                            break;
                        case 'Cert':
                            normalized.certifications.push(lineItem);
                            break;
                        default:
                            break;
                    }
                });
            }
        });
        normalized.events_no_results = normalized.events.length === 0;
        normalized.dues_no_results = normalized.dues.length === 0;
        normalized.certifications_no_results = normalized.certifications.length === 0;
        normalized.donations_no_results = normalized.donations.length === 0;
    }

    return normalized;
};

export default BillingPaymentHistoryLayout;
